import React, { useState, useEffect, memo } from 'react'
import { SnackBar } from '../..'
import { Severity } from '../../../../types'
import { formatDateForDisplay, removeMinYear } from '../../../../utils/datetime'
import { useUserItems, useNoteTypes } from '../../../../hooks'
import { AddEditNoteModal } from '../../../views/notes/addEditNoteModal'
import { Item } from '../../../../hooks/useUserItems/useUserItems.type'
import { Option } from '../../form/fields/dropdown/types'
import { Portal } from 'react-portal'

import { NotesProps, NoteUpdateStatus } from './types'
import * as styles from '../crudItems.module.scss'
import { NoteCopiedModal } from '../../../views/notes/noteCopiedModal'

const defaultItem = {
   ID: '',
   Title: '',
   Barcode: '',
   SerialNumber: '',
   CustomText1Value: '',
   ToolLoanId: '',
}

const Notes = ({
   itemId,
   toolLoanId,
   itemNotesData,
   refetchTableData,
   isNewItemNote = false,
   setIsNewItemNote,
   crudItemsParentPageView,
}: NotesProps) => {
   const { list: items } = useUserItems()
   const { list: noteTypes } = useNoteTypes()

   const [isEditNoteModalOpen, setIsEditNoteModalOpen] =
      useState<boolean>(false)
   const [selectedNote, setSelectedNote] = useState<any>()
   const [selectedItem, setSelectedItem] = useState<Item | null>(null)
   const [selectedNoteType, setSelectedNoteType] = useState<Option>()
   const [updateStatus, setUpdateStatus] = useState<NoteUpdateStatus>({
      IS_UPDATE_SUCCESS: false,
      IS_UPDATE_ERROR: false,
   })
   const [regeneratedNoteInfo, setRegeneratedNoteInfo] = useState(null)

   useEffect(() => {
      if (itemId && !selectedItem?.ID) {
         const item = items.find((item) => item.ID === itemId.toString())
         setSelectedItem(item)
      }
   })

   useEffect(() => {
      if (isNewItemNote) {
         setIsEditNoteModalOpen(true)
      }
   }, [isNewItemNote])

   useEffect(() => {
      if (selectedNote) {
         const noteType = noteTypes.find(
            (noteType) => noteType.Id === selectedNote?.NoteTypeId
         )
         setSelectedNoteType(
            selectedNote?.NoteTypeId && !!noteType
               ? {
                    value: noteType?.Id.toString(),
                    label: noteType?.Title,
                 }
               : null
         )
      }
   }, [selectedNote])

   const handleNoteClick = (selectedNoteId: number) => {
      const selectedNote = itemNotesData.find(
         (note) => note.Id === selectedNoteId
      )
      setSelectedNote(selectedNote)
      setIsEditNoteModalOpen(true)
   }

   const getUpdateStatus = (status: NoteUpdateStatus) => setUpdateStatus(status)

   const handleOpenRegeneratedNote = (note) => {
      // Add regenerated note properties to note
      setSelectedNote({ ...note, isRegeneratedNote: true }) 
      prePopulateEditNote({ ...note, isRegeneratedNote: true }, items)
      setRegeneratedNoteInfo(null)
      setIsEditNoteModalOpen(true)
   }

   const prePopulateEditNote = (note: any, items: Item[]) => {
      const noteType = noteTypes.find(
         (noteType) => noteType.Id === note?.NoteTypeId
      )
      const item = items.find(
         (item) => item?.ID.toString() === note?.ToolId.toString()
      )
      setSelectedNote(note)
      setSelectedNoteType(
         !!note?.NoteTypeId && !!noteType
            ? {
                 value: noteType?.Id.toString(),
                 label: noteType?.Title,
              }
            : undefined
      )
      setSelectedItem(!!note?.ToolId && !!item ? item : defaultItem)
      setIsEditNoteModalOpen(true)
   }

   return (
      <>
         <SnackBar
            message={
               updateStatus?.IS_UPDATE_SUCCESS
                  ? 'Note Updated'
                  : 'Error Updating Note'
            }
            open={
               updateStatus?.IS_UPDATE_SUCCESS || updateStatus?.IS_UPDATE_ERROR
            }
            severity={
               updateStatus?.IS_UPDATE_SUCCESS
                  ? Severity.SUCCESS
                  : Severity.ERROR
            }
            disableAutoClose={false}
         />

         {itemNotesData?.length > 0 &&
            itemNotesData.map((item, _index) => (
               <button
                  type="button"
                  onClick={() => handleNoteClick(item.Id)}
                  className={styles.itemNotesContainer}
                  key={item.Id}
               >
                  <div className={styles.note}>
                     <div className={styles.columnOne}>
                        <p>
                           <strong>Sent by: </strong>
                           {item.CreatedByName || '-'}
                        </p>

                        <p>
                           <strong>Note Type: </strong>
                           {item.NoteTypeTitle || '-'}
                        </p>

                        <p>
                           <strong>Due Date: </strong>
                           {item.Completed ? (
                              <span>
                                 {removeMinYear(formatDateForDisplay(item.DueDate))}
                              </span>
                           ) : (
                              <span className={styles.notCompleted}>
                                 {removeMinYear(formatDateForDisplay(item.DueDate))}
                              </span>
                           )}
                        </p>

                        <p>
                           <strong>Completed: </strong>
                           {item.Completed ? 'Yes' : 'No'}
                        </p>

                        <p>
                           <strong>Completed Date: </strong>
                           {removeMinYear(formatDateForDisplay(item.CompletedDate)) ||
                              'N/A'}
                        </p>

                        <p>
                           <strong>Completed By: </strong>
                           {item.CompletedBy || 'N/A'}
                        </p>

                        <p>
                           <strong>Completed Cost: </strong>
                           {item.CompletedCost ? `${item.CompletedCost}` : '-'}
                        </p>

                        <p>
                           <strong>Notes: </strong>
                           {item.NoteText || 'N/A'}
                        </p>
                     </div>
                  </div>
               </button>
            ))}
         {isEditNoteModalOpen && (
            <Portal>
               <AddEditNoteModal
                  selectedNote={selectedNote}
                  setSelectedNote={setSelectedNote}
                  setIsNewItemNote={setIsNewItemNote}
                  isNewItemNote={isNewItemNote}
                  isEditModalOpen={isEditNoteModalOpen}
                  setIsEditModalOpen={setIsEditNoteModalOpen}
                  selectedItem={selectedItem}
                  setSelectedItem={setSelectedItem}
                  selectedNoteType={selectedNoteType}
                  setRegeneratedNoteInfo={setRegeneratedNoteInfo}
                  setSelectedNoteType={setSelectedNoteType}
                  refetchNotes={refetchTableData}
                  getUpdateStatus={getUpdateStatus}
                  crudItemsParentPageView={crudItemsParentPageView}
                  toolLoanId={toolLoanId}
                  noteTypes={noteTypes}
                  items={items}
               />
            </Portal>
         )}

         <NoteCopiedModal
            regeneratedNoteInfo={regeneratedNoteInfo}
            setRegeneratedNoteInfo={setRegeneratedNoteInfo}
            handleOpenRegeneratedNote={handleOpenRegeneratedNote}
         />
      </>
   )
}

export default memo(Notes)
